import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink, Loader2 } from "lucide-react";
import { PreviewResourceBody } from "@/components/preview/preview-resource-body";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { orpc } from "@/lib/rpc";

// Inline preview of a single resource, used inside template canvas and the
// unified onboarding dashboard when the viewer clicks a resource in the
// portal. Pulls from preview.getResourcePreview so Notion-backed content is
// hydrated lazily.
export function ResourcePreviewPane({
  templateId,
  resourceId,
  onBack,
}: {
  templateId: string;
  resourceId: string;
  onBack: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const queryOpts = orpc.preview.getResourcePreview.queryOptions({
    input: { templateId, resourceId },
  });
  const query = useQuery(queryOpts);

  const refreshNotion = useMutation({
    ...orpc.preview.refreshPreviewContent.mutationOptions(),
    onSuccess: (fresh) => {
      queryClient.setQueryData(queryOpts.queryKey, fresh);
      toast({ title: "Conteúdo atualizado a partir do Notion." });
    },
    onError: () =>
      toast({ title: "Não foi possível sincronizar com o Notion.", variant: "destructive" }),
  });

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (query.error || !query.data) {
    return (
      <Card className="p-6 space-y-3">
        <p className="text-sm text-muted-foreground">Não foi possível carregar este conteúdo.</p>
        <Button variant="outline" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Voltar
        </Button>
      </Card>
    );
  }

  const data = query.data;
  const isRedirect = data.behavior === "redirect" && data.sourceUrl;

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6" data-testid="preview-resource-pane">
      <Button variant="ghost" size="sm" onClick={onBack} data-testid="preview-resource-back">
        <ArrowLeft className="h-4 w-4 mr-1" /> Voltar à jornada
      </Button>
      <header>
        <h1 className="text-3xl font-semibold tracking-tight">{data.title}</h1>
      </header>
      {isRedirect ? (
        <Card className="flex flex-col items-center gap-4 p-8 text-center">
          <p className="text-sm text-muted-foreground">
            Este recurso é um link externo. No onboarding, o colaborador será redirecionado
            automaticamente.
          </p>
          <Button asChild>
            <a href={data.sourceUrl!} target="_blank" rel="noreferrer">
              Abrir link externo <ExternalLink className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </Card>
      ) : (
        <PreviewResourceBody
          data={data}
          onRefreshNotion={() => refreshNotion.mutate({ templateId, resourceId })}
          isRefreshingNotion={refreshNotion.isPending}
        />
      )}
    </div>
  );
}
